import { useRef, useState } from "react";
import { QRCode } from "antd";
import shareIcon from "../../assets/image/arrow-up-from-bracket-solid-full (1).svg";
import giftTemplate from "../../assets/template/invitacion-zocoticket3.png";
import { formatGiftDate } from "./giftDateUtils";
import {
  loadImage,
  dataUrlToBlob,
  drawRoundedRect,
  fitText,
  wrapTextLines,
} from "./giftHelpers";
import { IconPencil, IconTrash } from "./giftIcons";
import GiftEditModal from "./GiftEditModal";
import GiftDeleteConfirm from "./GiftDeleteConfirm";

const GiftTicketCard = ({ ticket, order, qrIcon, onChanged }) => {
  const qrRef = useRef(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Order-level guard: the API rejects the whole invitation when ANY of its
  // tickets is redeemed, so a per-ticket check would leave siblings looking
  // editable and fail with a 409 only after the user filled the form.
  const isLocked = (order?.tickets || []).some((t) => t.status === "redeemed");
  const ticketTypeName = order?.ticketTypeName || "General";
  const guestName =
    order?.recipientName ||
    order?.giftLabel ||
    order?.guestName ||
    `Entrada ${ticket?.seq || "-"}`;
  const createdAt = order?.createdAt ? new Date(order.createdAt) : null;
  const createdLabel = createdAt ? createdAt.toLocaleDateString() : "";
  const expiresAtRaw = ticket?.expiresAt || order?.giftExpiresAt || null;
  const expiresAt = expiresAtRaw ? new Date(expiresAtRaw) : null;
  const expiresValid = expiresAt && !Number.isNaN(expiresAt.getTime());
  const isExpired = expiresValid && expiresAt.getTime() < Date.now();
  const expiresLabel = expiresValid ? expiresAt.toLocaleString() : "Sin vencimiento";
  const statusLabel = ticket?.status === "redeemed" ? "Usada" : isExpired ? "Vencida" : "Vigente";
  const statusClass = ticket?.status === "redeemed" ? "is-used" : isExpired ? "is-expired" : "is-active";

  const buildShareImage = async () => {
    const fallbackQr = () => {
      const baseCanvas = qrRef.current?.querySelector("canvas");
      if (!baseCanvas || !baseCanvas.toDataURL) return null;
      return baseCanvas.toDataURL("image/png");
    };
    try {
      const canvas = qrRef.current?.querySelector("canvas");
      if (!canvas) return null;
      const qrDataUrl = canvas.toDataURL("image/png");
      const [qrImg, templateImg] = await Promise.all([loadImage(qrDataUrl), loadImage(giftTemplate)]);

      const w = templateImg.width || 900;
      const h = templateImg.height || 1200;
      const out = document.createElement("canvas");
      out.width = w;
      out.height = h;
      const ctx = out.getContext("2d");
      if (!ctx) return null;

      ctx.drawImage(templateImg, 0, 0, w, h);

      const guestName =
        order?.giftLabel ||
        order?.recipientName ||
        order?.guestName ||
        "";
      const title = guestName || order?.eventName || "Entrada ZOCO";
      const upper = String(title).trim();
      const eventTitle = order?.eventName || "Entrada gift";
      const dateValue =
        order?.eventStartDate ||
        order?.eventDate ||
        order?.startDate ||
        order?.createdAt;
      const { day: dateDay, month: dateMonth } = formatGiftDate(dateValue);

      const ticketBox = {
        x: w * 0.2,
        y: h * 0.255,
        w: w * 0.6,
        h: h * 0.62,
      };
      const infoLeft = ticketBox.x + ticketBox.w * 0.1;
      const infoRight = ticketBox.x + ticketBox.w * 0.9;

      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      const titleFont = fitText(ctx, upper, w - 140, "600 56px 'Montserrat', sans-serif", 26);
      ctx.font = titleFont;
      ctx.fillText(upper, w / 2, h * 0.075);

      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.font = "400 47px 'Montserrat', sans-serif";
      const eventMaxWidth = 170;
      const eventLines = wrapTextLines(ctx, eventTitle, eventMaxWidth);
      const lineHeight = 52;
      let linesToDraw = eventLines.slice(0, 2);
      if (eventLines.length > 2) {
        let last = `${linesToDraw[1]} ${eventLines.slice(2).join(" ")}`.trim();
        while (last.length > 1 && ctx.measureText(`${last}…`).width > eventMaxWidth) {
          last = last.slice(0, -1);
        }
        linesToDraw[1] = `${last}…`;
      }
      linesToDraw.forEach((lineText, idx) => {
        ctx.fillText(lineText, infoLeft, ticketBox.y + ticketBox.h * 0.03 + idx * lineHeight);
      });

      if (dateDay || dateMonth) {
        ctx.textAlign = "right";
        ctx.fillStyle = "#ffffff";
        ctx.font = "400 47px 'Montserrat', sans-serif";
        ctx.fillText(`${dateDay}/`, infoRight, ticketBox.y + ticketBox.h * 0.03);
        ctx.fillText(dateMonth, infoRight, ticketBox.y + ticketBox.h * 0.095);
      }

      ctx.textAlign = "center";
      const qrBoxSize = ticketBox.w * 0.7;
      const qrBoxX = ticketBox.x + (ticketBox.w - qrBoxSize) / 2;
      const qrBoxY = ticketBox.y + ticketBox.h * 0.33;
      ctx.fillStyle = "#ffffff";
      drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 26);
      ctx.fill();
      ctx.drawImage(qrImg, qrBoxX + 22, qrBoxY + 22, qrBoxSize - 44, qrBoxSize - 44);

      return out.toDataURL("image/png");
    } catch (_) {
      return fallbackQr();
    }
  };

  const handleDownload = async () => {
    try {
      const url = await buildShareImage();
      if (!url) return;
      const link = document.createElement("a");
      link.href = url;
      link.download = `zoco-gift-${ticket?.ticketId || ticket?.seq || "qr"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (_) {}
  };

  const getQrBlob = async () => {
    const dataUrl = await buildShareImage();
    if (!dataUrl) return null;
    const blob = dataUrlToBlob(dataUrl);
    if (blob) return blob;
    const baseCanvas = qrRef.current?.querySelector("canvas");
    if (!baseCanvas || !baseCanvas.toDataURL) return null;
    return new Promise((resolve) => {
      baseCanvas.toBlob((next) => resolve(next), "image/png");
    });
  };

  const handleShare = async () => {
    try {
      const blob = await getQrBlob();
      if (!blob) return handleDownload();
      const file = new File([blob], `zoco-entrada-${ticket?.seq || "qr"}.png`, {
        type: "image/png",
      });
      const title = order?.eventName ? `Entrada para ${order.eventName}` : "Entrada Zoco";
      const text = order?.giftLabel ? `Entrada para ${order.giftLabel}` : "Entrada Zoco";
      if (navigator?.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await navigator.share({ files: [file], title, text });
        return;
      }
      if (navigator?.share) {
        await navigator.share({ title, text });
        return;
      }
      handleDownload();
    } catch (_) {}
  };

  return (
    <div className="zoco-qr-col">
      <article className="zoco-ticket">
        <div className="zoco-ticket-qr" ref={qrRef}>
          {ticket?.staticQrToken ? (
            <QRCode
              value={ticket.staticQrToken}
              size={165}
              icon={qrIcon}
              iconSize={38}
              errorLevel="H"
              bordered={false}
              bgColor="#ffffff"
              color="#111111"
            />
          ) : (
            <div className="zoco-ticket-qr zoco-ticket-qr--placeholder">
              <span>QR no disponible</span>
            </div>
          )}
        </div>
        <div className="zoco-ticket-divider" aria-hidden="true">
          <span className="zoco-ticket-notch left" />
          <span className="zoco-ticket-notch right" />
        </div>
        <div className="zoco-ticket-body">
          <div className="zoco-ticket-head">
            <div className="zoco-ticket-name">{guestName}</div>
          </div>
          <div className="zoco-ticket-meta" aria-label="Detalles de la entrada">
            <div className="col-k">
              <div className="k">Evento</div>
              <div className="v">{order?.eventName || "-"}</div>
            </div>
            <div className="col-k">
              <div className="k">Tipo</div>
              <div className="v">{ticketTypeName}</div>
            </div>
            <div className="col-k">
              <div className="k">Vencimiento</div>
              <div className="v">{expiresLabel}</div>
            </div>
          </div>
        </div>
      </article>
      <div className="zoco-ticket-actions">
        <button type="button" className="zoco-ticket-btn dark" onClick={handleDownload}>
          Descargar QR
        </button>
        <button
          type="button"
          className="zoco-ticket-btn light"
          onClick={handleShare}
          disabled={!ticket?.staticQrToken}
        >
          <span className="zoco-share">
            <img src={shareIcon} alt="" />
          </span>
          Compartir
        </button>
      </div>
      <div className="zoco-ticket-manage">
        <button
          type="button"
          className="zoco-ticket-icon-btn"
          aria-label="Editar invitación"
          title="Editar invitación"
          onClick={() => setShowEdit(true)}
          disabled={isLocked}
        >
          <IconPencil size={18} />
        </button>
        <button
          type="button"
          className="zoco-ticket-icon-btn danger"
          aria-label="Eliminar invitación"
          title="Eliminar invitación"
          onClick={() => setShowDelete(true)}
          disabled={isLocked}
        >
          <IconTrash size={18} />
        </button>
        {isLocked && (
          <span className="zoco-ticket-locked">La invitación ya fue utilizada</span>
        )}
      </div>
      <GiftEditModal
        show={showEdit}
        order={order}
        onHide={() => setShowEdit(false)}
        onSaved={onChanged}
      />
      <GiftDeleteConfirm
        show={showDelete}
        order={order}
        onHide={() => setShowDelete(false)}
        onDeleted={onChanged}
      />
    </div>
  );
};

export default GiftTicketCard;
